/*
 * The widget catalog - the curated set every dashboard starts from.
 *
 * These are the reference widgets: what an author copies, what the marketplace
 * seeds from, and what the authoring brief points at when it says "pick one and
 * configure it rather than writing a new one". They are config-driven on
 * purpose - a tank and a battery and a silo are one widget with a different
 * `shape`, and discovering that late means three widgets to fix instead of one.
 *
 * Each is a module body: it receives the SDK as globals and returns
 * { meta, setup }. Nothing here may reach a DOM, a network or a timer - the
 * worker it runs in has none of them.
 *
 * COLOURS ARE TOKENS ONLY. A literal is dropped by the renderer, so a widget
 * that writes one renders wrong rather than off-brand. Neutrals carry the
 * widget; --amber and --red are reserved for genuinely abnormal conditions,
 * because a screen where everything is coloured has nothing left to raise an
 * alarm with.
 */

import { METER } from './ideas/meter';
import type { SettingSpec } from './runtime/sdk';

export interface CatalogEntry {
  id: string;
  title: string;
  /** One line, shown on the picker card and read by the authoring agent. */
  summary: string;
  /** Config keys an author is expected to set, in the order a form shows them. */
  configKeys: string[];
  /** Sensible defaults, so a freshly placed tile renders something immediately. */
  defaults: Record<string, unknown>;
  minW: number;
  minH: number;
  source: string;
  /** For a native tile, the settings the base widget's dialog offers (a sandbox widget declares its own in meta). */
  settings?: SettingSpec[];
}

/* ------------------------------------------------------------------- fill -- */
// tank | battery | silo are the same widget; only the outline differs.

const FILL = `
const meta = { title: 'Fill', minWidth: 90, minHeight: 120, fps: 60 };
function setup(ctx) {
  const c = ctx.config;
  const read = ctx.sub(c.controllerId, c.symbolId);
  const lo = c.min == null ? 0 : Number(c.min);
  let hi = c.max == null ? 100 : Number(c.max);
  if (hi <= lo) hi = lo + 1;
  const span = hi - lo;
  const shape = c.shape === 'battery' ? 'battery' : 'tank';
  const low = c.low == null ? null : Number(c.low);
  const high = c.high == null ? null : Number(c.high);
  const overflow = lo + span * 0.97;
  const unit = c.unit == null ? '' : String(c.unit);
  const label = c.label == null ? '' : String(c.label);
  const dec = span >= 50 ? 0 : 1;
  // Motion: the shown level glides to the reading; slosh energy rises while it moves and decays
  // when it settles; the surface tilts the way the liquid is going. The waves themselves travel
  // at ONE steady pace — the churn changes how tall they are, never how fast they run.
  let shown = null, energy = 0, tilt = 0, lastT = 0, prevShown = null;
  const ease = (cur, target, rate, dt) => cur + (target - cur) * (1 - Math.exp(-rate * dt));
  return function () {
    const b = ctx.size();
    const W = b.width, H = b.height;
    const r = read();
    const known = r.num !== null;
    const v = known ? r.num : lo;
    const f = clamp(normalize(v, lo, hi), 0, 1);
    const now = Date.now();
    const dt = lastT ? Math.min(0.05, (now - lastT) / 1000) : 0;
    lastT = now;
    if (shown === null) { shown = f; prevShown = f; }
    else shown = ease(shown, f, 6, dt);
    const dv = shown - prevShown; prevShown = shown;
    energy = ease(energy, clamp(Math.abs(dv) * 140, 0, 1), 4, dt);
    tilt = ease(tilt, clamp(dv * 220, -1, 1), 5, dt);
    // The level's colour, and the readout's: a tank is blue unless a limit is crossed; a battery
    // is green, amber, red as it drains, and the number says so in the same colour.
    const level = shape === 'battery'
      ? (!known ? '--gray' : f < 0.2 ? '--red' : f < 0.5 ? '--amber' : '--green')
      : (!known ? '--gray' : v >= overflow ? '--red' : (high != null && v >= high) || (low != null && v <= low) ? '--amber' : '--blue');
    const tok = level;
    // LAYOUT IN PIXELS, sized to the tile: the vessel takes what is left above the readout and the
    // caption, so a small tile shows a small vessel with readable text and a large one a large
    // vessel with text that stays text-sized.
    const S = Math.min(W, H);
    const valueSize = clamp(S * 0.13, 12, 22);
    const labelSize = clamp(S * 0.075, 10, 13);
    const pad = clamp(S * 0.06, 6, 12);
    const textH = valueSize + (label ? labelSize + 4 : 0) + 6;
    const capH = shape === 'battery' ? Math.max(4, S * 0.03) : 0;
    const vh = Math.max(28, H - pad * 2 - textH - capH);
    const vw = clamp(vh * 0.64, 24, W - pad * 2);
    const T = { x: (W - vw) / 2, y: pad + capH, w: vw, h: vh };
    const wall = clamp(vw * 0.04, 1.5, 3);
    const rr = shape === 'battery' ? vw * 0.11 : vw * 0.22;
    const I = { x: T.x + wall + 1, y: T.y + wall + 1, w: T.w - 2 * (wall + 1), h: T.h - 2 * (wall + 1) };
    const top = I.y, bot = I.y + I.h, ir = Math.max(1, rr - wall - 1);
    const y0 = bot - clamp(shown, 0, 1) * (bot - top);
    const nodes = [];
    nodes.push(el('rect', { x: I.x, y: I.y, width: I.w, height: I.h, rx: ir, fill: '--bg-tertiary' }));
    if (shape === 'battery') {
      if (shown > 0.005) nodes.push(el('rect', { x: I.x + 1, y: y0, width: I.w - 2, height: Math.max(0, bot - y0 - 1), rx: Math.max(1, ir * 0.5), fill: tok, 'fill-opacity': 0.85 }));
      for (const k of [0.2, 0.4, 0.6, 0.8]) {
        const yy = bot - k * (bot - top);
        nodes.push(el('line', { x1: I.x, y1: yy, x2: I.x + I.w, y2: yy, stroke: '--border-color', 'stroke-width': wall }));
      }
      nodes.push(el('rect', { x: T.x + T.w * 0.34, y: T.y - capH, width: T.w * 0.32, height: capH, rx: capH * 0.3, fill: '--border-color' }));
    } else {
      // Two waves ride the surface, taller while the liquid is churned, always at the same slow pace.
      const amp = lerp(0.008, 0.05, energy) * I.w;
      const tiltPx = tilt * lerp(0.04, 0.14, energy) * I.w;
      const phase = now / 900;
      const k1 = 19 / I.w, k2 = 11 / I.w;
      const x1 = I.x, x2 = I.x + I.w;
      const step = Math.max(2, I.w / 24);
      const surface = (ph, a, tp) => {
        let d = '';
        for (let x = x1; x <= x2 + 0.01; x += step) {
          const t = (x - x1) / I.w;
          const y = y0 + Math.sin(x * k1 + ph) * a + Math.sin(x * k2 - ph * 0.7) * a * 0.55 + (t - 0.5) * tp;
          d += (d ? ' L' : 'M') + x.toFixed(2) + ' ' + Math.max(top, Math.min(bot, y)).toFixed(2);
        }
        d += ' L' + x2.toFixed(2) + ' ' + Math.max(top, Math.min(bot, y0 + Math.sin(x2 * k1 + ph) * a + Math.sin(x2 * k2 - ph * 0.7) * a * 0.55 + tp * 0.5)).toFixed(2);
        return d;
      };
      const surf = surface(phase, amp, tiltPx);
      const body = surf + ' L' + x2 + ' ' + (bot - ir) + ' Q' + x2 + ' ' + bot + ' ' + (x2 - ir) + ' ' + bot +
        ' L' + (x1 + ir) + ' ' + bot + ' Q' + x1 + ' ' + bot + ' ' + x1 + ' ' + (bot - ir) + ' Z';
      if (shown > 0.005) {
        nodes.push(el('path', { d: body, fill: tok, 'fill-opacity': 0.42 }));
        const deepY = Math.min(bot, y0 + (bot - y0) * 0.45);
        nodes.push(el('rect', { x: x1, y: deepY, width: I.w, height: Math.max(0, bot - deepY), rx: ir, fill: tok, 'fill-opacity': 0.28 }));
        nodes.push(el('path', { d: surf, fill: 'none', stroke: tok, 'stroke-width': clamp(I.w * 0.035, 1.5, 2.5), 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-opacity': 0.95 }));
        nodes.push(el('path', { d: surface(phase + 0.8, amp * 0.7, tiltPx * 0.7), fill: 'none', stroke: tok, 'stroke-width': clamp(I.w * 0.018, 0.8, 1.3), 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-opacity': 0.5 }));
      }
      for (const k of [0.25, 0.5, 0.75]) {
        const yy = bot - k * (bot - top);
        nodes.push(el('line', { x1: x2 - I.w * 0.08, y1: yy, x2: x2, y2: yy, stroke: '--border-color', 'stroke-width': 1 }));
      }
    }
    nodes.push(el('rect', { x: T.x, y: T.y, width: T.w, height: T.h, rx: rr, fill: 'none', stroke: '--border-color', 'stroke-width': wall }));
    // The readout in the app's own type: value in the level's colour, the caption quiet below it.
    const ty = T.y + T.h + 6 + valueSize * 0.85;
    nodes.push(el('text', { x: W / 2, y: ty, 'text-anchor': 'middle', 'font-size': valueSize, 'font-weight': 600,
      fill: known ? tok : '--text-secondary' }, (known ? fixed(v, dec) : '--') + (unit ? ' ' + unit : '')));
    if (label) nodes.push(el('text', { x: W / 2, y: ty + labelSize + 4, 'text-anchor': 'middle', 'font-size': labelSize, 'font-weight': 500, fill: '--text-secondary' }, label));
    return el('svg', { viewBox: '0 0 ' + W + ' ' + H, width: W, height: H }, nodes);
  };
}
return { meta, setup };`;

/* ------------------------------------------------------------------- dial -- */
// An ANALOG NEEDLE METER, ported from the v1 keepers: a 240° face with major and
// minor ticks, numeric scale labels, a tapered pointer and a hub. The face is
// neutral; warn/danger thresholds, when configured, tint the part of the scale
// they cover, so the needle swinging into a tinted arc IS the alarm read.

const DIAL = `
const meta = { title: 'Dial', minWidth: 120, minHeight: 120, fps: 60 };
function setup(ctx) {
  const c = ctx.config;
  const read = ctx.sub(c.controllerId, c.symbolId);
  const lo = c.min == null ? 0 : Number(c.min);
  const hi0 = c.max == null ? 100 : Number(c.max);
  const hi = hi0 <= lo ? lo + 1 : hi0;
  const MAJ = Math.max(2, c.majorTicks == null ? 5 : Number(c.majorTicks));
  const MINOR = 4;
  const A0 = -120, SWEEP = 240;
  const warn = c.warn == null ? null : Number(c.warn);
  const danger = c.danger == null ? null : Number(c.danger);
  const unit = c.unit == null ? '' : String(c.unit);
  const dec = c.decimals == null ? ((hi - lo) >= 50 ? 0 : (hi - lo) >= 5 ? 1 : 2) : Number(c.decimals);
  const fmtTick = (x) => { const a = Math.abs(x); return a >= 1000 ? (x / 1000).toFixed(a >= 10000 ? 0 : 1) + 'k' : a >= 100 ? x.toFixed(0) : (Math.round(x) === x ? x.toFixed(0) : x.toFixed(1)); };
  const ang = (v) => A0 + SWEEP * clamp(normalize(v, lo, hi), 0, 1);
  // The pointer is a second-order spring in angle space: it seeks the reading, overshoots a hair
  // and settles - an instrument hand, not a snap. Advanced by wall-clock dt on the frame clock.
  let pos = A0, vel = 0, seeded = false, lastT = 0;
  return function () {
    const b = ctx.size();
    const r = read();
    const W = b.width, H = b.height, S = Math.min(W, H);
    const cx = W / 2, cy = H / 2 + S * 0.08, R = S * 0.40;
    const known = r.num !== null;
    const target = known ? ang(r.num) : A0;
    const now = Date.now();
    const dt = lastT ? Math.min(0.05, (now - lastT) / 1000) : 0;
    lastT = now;
    if (!seeded) { pos = target; vel = 0; seeded = true; }
    else if (dt > 0) { const acc = 120 * (target - pos) - 13 * vel; vel += acc * dt; pos += vel * dt; }
    const nodes = [];
    nodes.push(el('path', { d: arc(cx, cy, R, A0, A0 + SWEEP), fill: 'none', stroke: '--bg-tertiary', 'stroke-width': S * 0.03, 'stroke-linecap': 'round' }));
    nodes.push(el('path', { d: arc(cx, cy, R, A0, A0 + SWEEP), fill: 'none', stroke: '--border-color', 'stroke-width': 1, 'stroke-linecap': 'round', opacity: 0.6 }));
    const rA = R + S * 0.014;
    if (warn != null) {
      const a1 = ang(warn), a2 = danger != null ? ang(danger) : A0 + SWEEP;
      if (a2 > a1 + 0.5) nodes.push(el('path', { d: arc(cx, cy, rA, a1, a2), fill: 'none', stroke: '--amber', 'stroke-opacity': 0.6, 'stroke-width': S * 0.018 }));
    }
    if (danger != null) {
      const a1 = ang(danger);
      if (A0 + SWEEP > a1 + 0.5) nodes.push(el('path', { d: arc(cx, cy, rA, a1, A0 + SWEEP), fill: 'none', stroke: '--red', 'stroke-opacity': 0.65, 'stroke-width': S * 0.018 }));
    }
    const total = MAJ * (MINOR + 1);
    for (let i = 0; i <= total; i++) {
      const major = i % (MINOR + 1) === 0;
      const a = A0 + SWEEP * (i / total);
      const po = polar(cx, cy, R, a), pi = polar(cx, cy, R - S * (major ? 0.075 : 0.045), a);
      nodes.push(el('line', { x1: po[0], y1: po[1], x2: pi[0], y2: pi[1], stroke: '--text-secondary',
        'stroke-width': major ? S * 0.012 : S * 0.006, 'stroke-linecap': 'round', opacity: major ? 0.85 : 0.45 }));
      if (major) {
        const lp = polar(cx, cy, R - S * 0.14, a);
        nodes.push(el('text', { x: lp[0], y: lp[1], 'text-anchor': 'middle', 'dominant-baseline': 'central',
          'font-size': S * 0.058, 'font-weight': 500, fill: '--text-secondary' }, fmtTick(lo + (hi - lo) * i / total)));
      }
    }
    const tip = R - S * 0.06, tail = S * 0.085, hw = S * 0.026;
    nodes.push(el('g', { transform: 'rotate(' + pos.toFixed(2) + ' ' + cx.toFixed(2) + ' ' + cy.toFixed(2) + ')', fill: '--text-primary', opacity: known ? 1 : 0.35 }, [
      el('polygon', { points: cx.toFixed(2) + ',' + (cy - tip).toFixed(2) + ' ' + (cx - hw).toFixed(2) + ',' + (cy + tail * 0.15).toFixed(2) + ' ' + (cx + hw).toFixed(2) + ',' + (cy + tail * 0.15).toFixed(2) }),
      el('rect', { x: cx - hw * 0.7, y: cy, width: hw * 1.4, height: tail, rx: hw * 0.6 }),
    ]));
    nodes.push(el('circle', { cx, cy, r: S * 0.055, fill: '--bg-tertiary', stroke: '--border-color', 'stroke-width': S * 0.01 }));
    nodes.push(el('circle', { cx, cy, r: S * 0.018, fill: '--text-secondary' }));
    nodes.push(el('text', { x: cx, y: cy + S * 0.2, 'text-anchor': 'middle', 'dominant-baseline': 'central',
      'font-size': clamp(S * 0.1, 12, 26), 'font-weight': 600,
      fill: '--text-primary' }, (known ? fixed(r.num, dec) : '--') + (unit ? ' ' + unit : '')));
    if (c.label) nodes.push(el('text', { x: cx, y: H - 6, 'text-anchor': 'middle', 'font-size': clamp(S * 0.075, 10, 13), 'font-weight': 500, fill: '--text-secondary' }, String(c.label)));
    return el('svg', { viewBox: '0 0 ' + W + ' ' + H, width: W, height: H }, nodes);
  };
}
return { meta, setup };`;


export const CATALOG: CatalogEntry[] = [
  {
    id: 'fill', title: 'Fill', summary: 'A level in a vessel: a tank with a live, settling surface and graduations, or a segmented battery, by shape. Blue by default; amber below `low` or above `high` when set, red near overflow.',
    configKeys: ['controllerId', 'symbolId', 'min', 'max', 'low', 'high', 'shape', 'unit', 'label'],
    defaults: { min: 0, max: 100, shape: 'tank' }, minW: 2, minH: 3, source: FILL,
  },
  {
    id: 'dial', title: 'Dial', summary: 'An analog needle meter: a 240-degree face with ticks and scale labels and a pointer. Set warn and danger to tint the part of the scale they cover.',
    configKeys: ['controllerId', 'symbolId', 'min', 'max', 'warn', 'danger', 'unit', 'label', 'decimals', 'majorTicks'],
    defaults: { min: 0, max: 100, majorTicks: 5 }, minW: 3, minH: 3, source: DIAL,
  },
  {
    id: 'meter', title: 'Meter', summary: 'A needle on an arc for temperature, rpm, pressure or level: min/max, unit, decimals, tone bands drawn outside the arc, a full (240°) or half (180°) arc.',
    configKeys: ['controllerId', 'symbolId', 'label', 'min', 'max', 'unit', 'decimals', 'bands', 'ticks', 'arc'],
    defaults: { min: 0, max: 100, ticks: 5, arc: 240 }, minW: 2, minH: 2, source: METER,
  },
  {
    /*
     * NATIVE: the app's own list rows (the ones MCP servers and model providers are listed with),
     * one per point that is beyond a limit or within its margin, oldest first. Config is data only.
     */
    id: 'notifications', title: 'Notifications', summary: 'Every watched point outside its high/low limit (or within `margin` of one), oldest first, as the app’s own list rows: label, which limit, the value, how long.',
    configKeys: ['items'],
    defaults: { items: [] }, minW: 3, minH: 2, source: 'native:alarms',
    settings: [{ key: 'items', label: 'Watched points', kind: 'list', fields: [
      { key: 'label', label: 'Label', kind: 'text' }, { key: 'controllerId', label: 'Controller', kind: 'text' }, { key: 'symbolId', label: 'Symbol', kind: 'text' },
      { key: 'high', label: 'High', kind: 'number' }, { key: 'low', label: 'Low', kind: 'number' }, { key: 'margin', label: 'Margin', kind: 'number' }, { key: 'unit', label: 'Unit', kind: 'text' },
    ] }],
  },
  {
    /*
     * NATIVE, like the map: the chart is the app's own <vitesse-chart> (the UniversalChart on the
     * Lit substrate — axes, gradient fill, crosshair, smooth on the shared chart clock), fed by the
     * page's value fanout. A sandboxed scene tree cannot give that, and a second chart would be a
     * second chart to keep in step. Config is DATA only: which symbol, how long a window, a fixed
     * y range if wanted.
     */
    id: 'chart', title: 'Chart', summary: 'A live time-series chart: axis, grid, gradient fill and crosshair, smooth at frame rate. Bind ONE value with controllerId/symbolId, or overlay SEVERAL traces on the one chart as symbols: [{controllerId, symbolId, label}, …]. windowSec sets how much history is shown; min/max pin the y axis.',
    configKeys: ['controllerId', 'symbolId', 'symbols', 'windowSec', 'min', 'max', 'label', 'theme'],
    defaults: { windowSec: 120 }, minW: 4, minH: 3, source: 'native:chart',
  },
  {
    /*
     * NATIVE: a rich-text note in the document's own type, edited with the app's editor. Its
     * config carries the HTML (`content`), sanitized on both sides; nothing runs.
     */
    id: 'note', title: 'Note', summary: 'A rich-text note: headings, lists, a checklist you can tick on the board, code and highlights. Edit from the tile. content is Markdown (or HTML from the editor).',
    configKeys: ['content'],
    defaults: { content: '' }, minW: 3, minH: 3, source: 'native:note',
  },
  {
    /*
     * NATIVE: the model page's commands section as a tile — the declared commands of ONE model as
     * the app's command buttons. Config is the model id.
     */
    id: 'model-commands', title: 'Model commands', summary: "One model's declared commands as buttons, exactly the model page's Commands panel. modelId picks the model.",
    configKeys: ['modelId'],
    defaults: {}, minW: 3, minH: 2, source: 'native:model-commands',
  },
  {
    /*
     * NATIVE: the model page's statechart renderer as a tile — the live state chart of ONE model,
     * the active state lit. Config is the model id.
     */
    id: 'model-statechart', title: 'Model statechart', summary: "One model's live statechart, the active state lit, exactly the model page's Statechart panel. modelId picks the model.",
    configKeys: ['modelId'],
    defaults: {}, minW: 4, minH: 4, source: 'native:model-statechart',
  },
  {
    /*
     * NATIVE: its source names an app component instead of
     * carrying module code, because a map cannot exist inside the sandbox (no
     * network for tiles, no DOM for a GL canvas — the sandbox doing its job).
     * The tile IS the fleet map — the same component as the fleet app's Map
     * page, fed the server-scoped roster (+ private servers for super-admins).
     * The one config surface: `markers` — hand-placed labelled pins
     * ([{ label, lat, lng }]), for marking sites/areas the fleet data alone
     * can't name. The fleet itself is never configurable: it is always the
     * real roster.
     */
    id: 'map', title: 'Map', summary: "Geography - the org's relays as live health pins on the world map (the fleet map as a tile), plus optional labelled marker pins: markers: [{ label, lat, lng }].",
    configKeys: ['markers'],
    defaults: { markers: [] }, minW: 4, minH: 3, source: 'native:map',
  },
];

/** Find a catalog entry by its widget SOURCE (what a marketplace widget carries),
 *  not its catalog id — so an install can resolve minW/minH/defaults. */
export function catalogForSource(source: string): CatalogEntry | undefined {
  return CATALOG.find((c) => c.source === source);
}

export function catalogEntry(id: string): CatalogEntry | undefined {
  return CATALOG.find((c) => c.id === id);
}
